import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { RotateCcw, Calendar, Search, Edit } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';

const PastMatchesPage = () => {
  const navigate = useNavigate();
  const { matches, tournament, setCurrentMatchId } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter completed matches
  const completedMatches = matches.filter(match => match.status === 'completed')
    .sort((a, b) => a.gameNumber - b.gameNumber);
  
  // Filter matches based on search term
  const filteredMatches = completedMatches.filter(match => {
    if (!searchTerm.trim()) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      match.homeTeam.name.toLowerCase().includes(term) ||
      match.awayTeam.name.toLowerCase().includes(term) ||
      match.fieldName.toLowerCase().includes(term) ||
      `game ${match.gameNumber}`.toLowerCase().includes(term)
    );
  });
  
  // Navigate to match editing
  const editMatch = (matchId) => {
    setCurrentMatchId(matchId);
    navigate(`/match/${matchId}`);
  };
  
  // Format time string from minutes
  const getTimeString = (startTime) => {
    const hours = Math.floor(startTime / 60);
    const minutes = startTime % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="px-4 py-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-4 flex items-center">
        <RotateCcw size={24} className="mr-2" />
        Past Matches
      </h1>
      
      {/* Search bar */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search past matches..."
          className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
        />
      </div>
      
      {filteredMatches.length === 0 ? (
        <Card className="p-6 text-center">
          <Calendar size={24} className="mx-auto mb-2 text-gray-400" />
          <h2 className="text-lg font-medium mb-2">No past matches found</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {completedMatches.length === 0 
              ? "Once matches are completed, they'll appear here for review and editing."
              : "No matches match your search criteria."}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredMatches.map(match => (
            <Card key={match.id} className="overflow-hidden">
              <div className="border-b dark:border-gray-700 px-4 py-2 bg-gray-50 dark:bg-gray-800 flex justify-between items-center">
                <div className="flex items-center">
                  <span className="font-semibold mr-3">Game {match.gameNumber}</span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">{match.fieldName}</span>
                </div>
                <Button
                  small
                  secondary
                  icon={<Edit size={14} />}
                  onClick={() => editMatch(match.id)}
                >
                  Edit
                </Button>
              </div>
              
              <div className="p-4">
                <div className="flex justify-between items-center">
                  {/* Home team */}
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: match.homeTeam.color }}
                    />
                    <span className="font-medium">{match.homeTeam.name}</span>
                  </div>
                  
                  {/* Score */}
                  <div className="font-bold text-lg mx-4">
                    {match.homeScore} - {match.awayScore}
                  </div>
                  
                  {/* Away team */}
                  <div className="flex items-center">
                    <span className="font-medium">{match.awayTeam.name}</span>
                    <div 
                      className="w-3 h-3 rounded-full ml-2" 
                      style={{ backgroundColor: match.awayTeam.color }}
                    />
                  </div>
                </div>
                
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {match.round.startsWith('group-')
                    ? `Group ${match.round.replace('group-', '')}`
                    : match.round.startsWith('knockout-')
                      ? `Knockout Stage`
                      : match.round}
                  {' • '}
                  {getTimeString(match.startTime)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PastMatchesPage;
